import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Heart, Star } from "lucide-react";
import api from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import ProgressBar from "@/components/ProgressBar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function RouteDetail() {
  const { id } = useParams();
  const [route, setRoute] = useState(null);
  const [videos, setVideos] = useState([]);
  const { user } = useAuth();
  const [fav, setFav] = useState(false);

  useEffect(() => {
    api.get(`/routes/${id}`).then((r) => setRoute(r.data)).catch(() => {});
    api.get("/videos", { params: { route_id: id, limit: 100 } }).then((r) => setVideos(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (user) setFav((user.favorite_routes || []).includes(id));
  }, [user, id]);

  const toggleFav = async () => {
    if (!user) { toast.error("Login to favorite a route"); return; }
    try {
      const { data } = await api.post(`/users/me/favorite-route/${id}`);
      setFav(data.favorite);
      toast.success(data.favorite ? "Added to favorites" : "Removed from favorites");
    } catch {
      toast.error("Could not update favorites");
    }
  };

  if (!route) return <div className="max-w-7xl mx-auto px-4 py-24 text-zinc-400">Loading...</div>;

  return (
    <div data-testid={`route-detail-${id}`}>
      <div className="relative h-72 sm:h-96 overflow-hidden">
        <img src={route.character_image} alt={route.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <Link to="/routes" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" /> All routes
          </Link>
          <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter">{route.name}</h1>
          <p className="mt-3 max-w-2xl text-zinc-300">{route.description}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              data-testid="route-fav-btn"
              onClick={toggleFav}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border transition-all ${
                fav ? "bg-[#E50914]/20 border-[#E50914] text-[#E50914]" : "bg-white/5 border-white/10 hover:border-white/30 text-zinc-200"
              }`}
            >
              <Star className={`w-4 h-4 ${fav ? "fill-[#E50914]" : ""}`} /> {fav ? "Favorited" : "Favorite this route"}
            </button>
            <span className="text-xs text-zinc-400">{route.video_count} videos · {route.progress}% unlocked</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-12">
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2 text-xs">
            <span className="font-bold text-zinc-300 uppercase tracking-widest">Route Progress</span>
            <span className="text-[#32D74B] font-black">{route.progress}%</span>
          </div>
          <ProgressBar value={route.progress} max={100} testid="route-detail-progress" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <h2 className="font-display text-2xl font-black mb-6">Videos in this route</h2>
        {videos.length === 0 ? (
          <div className="text-zinc-500">No videos yet for this route.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videos.map((v) => <VideoCard key={v.id} video={v} route={route} />)}
          </div>
        )}
      </div>
    </div>
  );
}
